import { useEffect, useRef } from 'react'

/**
 * A factory function that returns a hook which registers a component to update the browser location search to reflect the feature value.
 * The hook enables deeplinking to features with a given value and preserving feature values across page refreshes.
 *
 * @param {string} feature - The feature to create a deeplink hook for. eg. 'tab', 'modal', 'filter'
 * @returns {Function}
 * @example
 * import { createUseFeatureDeepLink } from '@afterpaytouch/core'
 *
 * export const useTabDeepLink = createUseFeatureDeepLink('tab')
 */
export const createUseFeatureDeepLink = (feature: string) => {
  return (value: string | boolean, modifier: string, ignoreFirstRender: boolean = true) => {
    const firstUpdate = useRef(true)
    useEffect(() => {
      // Don't update the browser location on the first render
      if (ignoreFirstRender && firstUpdate.current) {
        firstUpdate.current = false
        return
      }
      // Uses URLSearchParams.set to avoid overwriting search params other than <feature>-<modifier>
      const params = new URLSearchParams(window.location.search)
      params.set(`${feature}-${modifier}`, value.toString())
      // To preserve animations we must use the History API instead of shallow router.push
      window.history.replaceState({}, '', `?${params.toString()}`)

      return () => {
        // Cleanup
        params.delete(`${feature}-${modifier}`)
        window.history.replaceState({}, '', `?${params.toString()}`)
      }
    }, [value, modifier, ignoreFirstRender])
  }
}
